/* Page Base Fetches */
import profile from "@/profiles/profileSingleton"
import { getSpaceNameCaps } from "@/lib/config/contentfulCodes"

export type ContentfulSpaceOverride = {
  spaceId: string
  environment?: string
}

export class ContentfulFetchError extends Error {
  constructor(
    message: string,
    public readonly context: {
      mode: string
      status?: number
      graphqlErrors?: unknown[]
    },
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = "ContentfulFetchError"
  }
}

// GraphQL error codes that represent expected content issues (e.g. a
// dangling reference to a deleted/unpublished entry) rather than a broken
// query — these should never fail the build.
const BENIGN_ERROR_CODES = new Set(["UNRESOLVABLE_LINK"])

const isBuildPhase = () =>
  process.env.NEXT_PHASE === "phase-production-build"

const hasHardErrors = (errors?: unknown[]) =>
  !!errors?.some(
    (e: any) => !BENIGN_ERROR_CODES.has(e?.extensions?.contentful?.code),
  )

export async function fetchGQL(
  mode: "mxp" | "rc" | "datastorage",
  query: any,
  preview = false,
  log = false,
  cacheTags?: string[],
  softFail = true,
  spaceOverride?: ContentfulSpaceOverride,
) {
  if (
    !spaceOverride &&
    mode === "mxp" &&
    (!profile.NEXT_PUBLIC_CF_MXP_SPACE_ID ||
      !profile.NEXT_PUBLIC_CF_MXP_ENVIRONMENT)
  ) {
    throw new Error(
      "Missing NEXT_PUBLIC_CF_MXP_SPACE_ID or NEXT_PUBLIC_CF_MXP_ENVIRONMENT in profile.",
    )
  }
  if (
    mode === "rc" &&
    (!profile.NEXT_PUBLIC_CF_RC_SPACE_ID ||
      !profile.NEXT_PUBLIC_CF_RC_ENVIRONMENT)
  ) {
    throw new Error(
      "Missing NEXT_PUBLIC_CF_RC_SPACE_ID or NEXT_PUBLIC_CF_RC_ENVIRONMENT in profile.",
    )
  }
  if (
    mode === "datastorage" &&
    (!profile.NEXT_PUBLIC_CF_DATASTORAGE_SPACE_ID ||
      !profile.NEXT_PUBLIC_CF_DATASTORAGE_ENV)
  ) {
    throw new Error(
      "Missing NEXT_PUBLIC_CF_DATASTORAGE_SPACE_ID or NEXT_PUBLIC_CF_DATASTORAGE_ENV in profile.",
    )
  }

  // Token
  let spaceName = spaceOverride
    ? getSpaceNameCaps(spaceOverride.spaceId)
    : getSpaceNameCaps(profile.NEXT_PUBLIC_CF_MXP_SPACE_ID)
  if (!spaceOverride && mode == "rc") {
    spaceName = getSpaceNameCaps(profile.NEXT_PUBLIC_CF_RC_SPACE_ID)
  }
  if (!spaceOverride && mode == "datastorage") {
    spaceName = getSpaceNameCaps(profile.NEXT_PUBLIC_CF_DATASTORAGE_SPACE_ID)
  }
  const token = preview
    ? process.env[`CONTENTFUL_${spaceName}_PREVIEW_TOKEN`]
    : process.env[`CONTENTFUL_${spaceName}_ACCESS_TOKEN`]
  if (!token) {
    throw new Error("Missing Contentful token in environment variables.")
  }

  // Space and Environment
  let spaceId, environment
  if (spaceOverride) {
    spaceId = spaceOverride.spaceId
    environment = spaceOverride.environment ?? "master"
  } else if (mode === "mxp") {
    spaceId = profile.NEXT_PUBLIC_CF_MXP_SPACE_ID
    environment = profile.NEXT_PUBLIC_CF_MXP_ENVIRONMENT
  } else if (mode === "rc") {
    spaceId = profile.NEXT_PUBLIC_CF_RC_SPACE_ID
    environment = profile.NEXT_PUBLIC_CF_RC_ENVIRONMENT
  } else if (mode === "datastorage") {
    spaceId = profile.NEXT_PUBLIC_CF_DATASTORAGE_SPACE_ID
    environment = profile.NEXT_PUBLIC_CF_DATASTORAGE_ENV
  }

  // Collapse insignificant whitespace (indentation/newlines from template
  // literals) before sending. GraphQL ignores it, but Contentful enforces an
  // 8192-byte cap on the raw query string — large queries (e.g. site nav,
  // with its deeply nested fragments) can tip over that limit on whitespace
  // alone and get rejected with QUERY_TOO_BIG.
  const minifiedQuery =
    typeof query === "string" ? query.replace(/\s+/g, " ").trim() : query

  const fetch_obj: any = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query: minifiedQuery }),
  }

  // Caching strategy:
  // - Live Preview: no-store (always fresh)
  // - Production with cacheTags: persists in Next.js Data Cache across all requests
  //   until busted via revalidateTag() in the webhook handler
  // - Production without cacheTags: cache indefinitely, busted by revalidatePath()
  if (profile.NEXT_PUBLIC_MODE === "live-preview") {
    fetch_obj.cache = "no-store"
  } else if (cacheTags?.length) {
    fetch_obj.next = { tags: cacheTags, revalidate: false }
  } else {
    fetch_obj.next = { revalidate: false }
  }

  if (log) {
    console.log(
      "fetchGQL:",
      `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`,
    )
    console.log("fetchGQL query: \n", query)
  }

  const url = `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`

  const maxAttempts = 4
  let response: Response | null = null
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    response = await fetch(url, fetch_obj)
    if (response.status !== 429 || attempt === maxAttempts - 1) {
      break
    }
    const retryAfterHeader = response.headers.get("retry-after")
    const retryAfterMs = retryAfterHeader
      ? Math.max(parseInt(retryAfterHeader, 10) || 0, 1) * 1000
      : Math.min(1000 * 2 ** attempt, 8000)
    console.warn(
      `[fetchGQL] HTTP 429 from Contentful ${mode}; retrying in ${retryAfterMs}ms (attempt ${attempt + 1}/${maxAttempts})`,
    )
    await new Promise(resolve => setTimeout(resolve, retryAfterMs))
  }

  if (!response) {
    throw new Error(`Contentful ${mode} fetch failed before a response was received`)
  }

  if (!response.ok) {
    let body: string | undefined
    try {
      body = await response.text()
    } catch {
      /* ignore unreadable body */
    }
    const msg = `Contentful ${mode} returned HTTP ${response.status}${body ? `: ${body.slice(0, 500)}` : ""}`
    const err = new ContentfulFetchError(msg, { mode, status: response.status })
    console.error(err)
    // During `next build`, always fail loudly so a broken build gets
    // caught instead of shipping a blank/error page. At runtime, honor
    // softFail so a transient Contentful issue doesn't take down a live page.
    if (softFail && !isBuildPhase()) {
      console.warn(`[fetchGQL softFail] ${msg}`)
      return null
    }
    throw err
  }

  const json = await response.json()

  if (!json.data && json.errors?.length) {
    const msg = `Contentful ${mode} GraphQL errors (no data): ${json.errors[0]?.message}`
    const err = new ContentfulFetchError(msg, {
      mode,
      graphqlErrors: json.errors,
    })
    console.error(err)
    if (softFail && !isBuildPhase()) {
      console.warn(`[fetchGQL softFail] ${msg}`)
      return null
    }
    throw err
  }

  // Partial success (data + errors): benign codes like UNRESOLVABLE_LINK are
  // logged by callers and ignored, but a real schema/field error here means
  // the query itself is broken — fail the build rather than ship a page with
  // silently missing sections.
  if (json.errors?.length && hasHardErrors(json.errors) && isBuildPhase()) {
    const msg = `Contentful ${mode} GraphQL errors (partial data): ${json.errors[0]?.message}`
    const err = new ContentfulFetchError(msg, {
      mode,
      graphqlErrors: json.errors,
    })
    console.error(err)
    throw err
  }

  return json
}

export class BuildDataError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "BuildDataError"
  }
}

/**
 * During production builds, throws so Next.js retries (staticGenerationRetryCount)
 * and ultimately fails the build. At runtime, logs without crashing.
 */
export function assertBuildData<T>(
  value: T | null | undefined,
  label: string,
): asserts value is T {
  if (value != null) return
  const msg = `[BuildDataError] Critical data missing: ${label}`
  if (process.env.NEXT_PHASE === "phase-production-build") {
    throw new BuildDataError(msg)
  }
  const err = new BuildDataError(msg)
  console.error(err)
}
